import { formatCPF, formatPhone, titleCase } from '@westhetics/ui'
import {
  formatDocNumber,
  isValidCNPJ,
  isValidCPF,
  normalizeDocNumber,
  onlyDigits,
} from '@/features/clinic-profile/doc-number'
import { emailError, formatPostalCode, personNameError } from '@/features/clinic-profile/form'
import { STATES } from '@/features/clinic-profile/labels'
import type { FormErrors } from '@/features/clinic-profile/use-form-state'
import type {
  Audience,
  ForeignDocumentType,
  Gender,
  ImageConsent,
  MaritalStatus,
  Patient,
  PatientInput,
  ReferralChannel,
} from './api'
import { brDateToIso, isoToBrDate, maskDate, todayIso } from '@/lib/dates'
import { DEFAULT_DIAL_CODE, formatDocument, splitWhatsapp } from './labels'

/**
 * O formulário do cadastro de paciente (docs/areas/pacientes.md, "Entidades e campos"). Os campos
 * ficam como a pessoa digita (com máscara); a forma do contrato sai dos montadores no fim do
 * arquivo. Só nome e WhatsApp impedem salvar (RN-PAC-01): o que falta do resto é assunto do
 * medidor (RN-PAC-09), não de erro de campo. As mensagens são as da API.
 */

export interface PatientFormState {
  /** O público da pessoa (RN-PAC-36): decide se o documento aceita CNPJ (RN-PAC-38). */
  audience: Audience
  name: string
  birthDate: string
  /** Estrangeira sem CPF: o documento estrangeiro entra no lugar (RN-PAC-04). */
  isForeign: boolean
  docNumber: string
  foreignDocumentType: ForeignDocumentType | ''
  foreignDocumentNumber: string
  foreignDocumentCountry: string
  rg: string
  maritalStatus: MaritalStatus | ''
  occupation: string
  gender: Gender
  dialCode: string
  whatsapp: string
  email: string
  instagram: string
  postalCode: string
  street: string
  number: string
  complement: string
  district: string
  city: string
  state: string
  country: string
  guardianName: string
  guardianDocNumber: string
  guardianWhatsapp: string
  imageConsent: ImageConsent
  referralChannel: ReferralChannel | ''
  referralDetail: string
  notes: string
}

export type PatientFormKey = keyof PatientFormState

export const EMPTY_PATIENT_FORM: PatientFormState = {
  audience: 'patient',
  name: '',
  birthDate: '',
  isForeign: false,
  docNumber: '',
  foreignDocumentType: '',
  foreignDocumentNumber: '',
  foreignDocumentCountry: '',
  rg: '',
  maritalStatus: '',
  occupation: '',
  gender: 'not_provided',
  dialCode: DEFAULT_DIAL_CODE,
  whatsapp: '',
  email: '',
  instagram: '',
  postalCode: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  country: 'BR',
  guardianName: '',
  guardianDocNumber: '',
  guardianWhatsapp: '',
  imageConsent: 'not_provided',
  referralChannel: '',
  referralDetail: '',
  notes: '',
}

export const PATIENT_FORM_KEYS = Object.keys(EMPTY_PATIENT_FORM) as PatientFormKey[]

/** O cadastro rápido do celular (RN-PAC-24). */
export const QUICK_FORM_KEYS = ['name', 'whatsapp'] as const satisfies readonly PatientFormKey[]

/** Os blocos da tela, para dizer onde estão as alterações que se perdem ao sair sem salvar. */
const BLOCKS: { label: string; keys: readonly PatientFormKey[] }[] = [
  {
    label: 'Identificação',
    keys: [
      'audience',
      'name',
      'birthDate',
      'isForeign',
      'docNumber',
      'foreignDocumentType',
      'foreignDocumentNumber',
      'foreignDocumentCountry',
      'rg',
    ],
  },
  { label: 'Estado civil e profissão', keys: ['maritalStatus', 'occupation', 'gender'] },
  { label: 'Contato', keys: ['dialCode', 'whatsapp', 'email', 'instagram'] },
  {
    label: 'Endereço',
    keys: ['postalCode', 'street', 'number', 'complement', 'district', 'city', 'state', 'country'],
  },
  { label: 'Responsável', keys: ['guardianName', 'guardianDocNumber', 'guardianWhatsapp'] },
  { label: 'Consentimentos', keys: ['imageConsent'] },
  { label: 'Origem e observações', keys: ['referralChannel', 'referralDetail', 'notes'] },
]

/** Os nomes dos blocos com alteração não salva: "Endereço e Contato". */
export function changedBlocks(values: PatientFormState, initial: PatientFormState): string[] {
  return BLOCKS.filter((block) => block.keys.some((key) => values[key] !== initial[key])).map(
    (block) => block.label,
  )
}

// As máscaras de data e a conversão dd/mm/aaaa ↔ aaaa-mm-dd moram em `@/lib/dates`.
export { brDateToIso, maskDate, todayIso }

// Máscaras --------------------------------------------------------------------------------------

/**
 * DDD e número no Brasil; de outro país, só os dígitos, sem máscara, até onde o número inteiro
 * (DDI e número) cabe nos 15 dígitos do E.164.
 */
export function maskWhatsapp(text: string, dialCode: string) {
  const digits = onlyDigits(text)
  if (dialCode === DEFAULT_DIAL_CODE) return formatPhone(digits.slice(0, 11))
  return digits.slice(0, 15 - onlyDigits(dialCode).length)
}

/**
 * A máscara do documento: paciente só tem CPF; aluno, funcionário e terceiro digitam CPF ou CNPJ, e a
 * máscara troca no 12º caractere (ou na primeira letra, da raiz alfanumérica; RT-05, RN-PAC-38).
 */
export function maskDocNumber(text: string, audience: Audience): string {
  return audience === 'patient' ? formatCPF(text) : formatDocNumber(text)
}

/** O nome de usuário, sem o @ e sem espaços. */
export function maskInstagram(text: string) {
  return text.replace(/^@+/, '').replace(/\s/g, '').slice(0, 30)
}

/** Menor de 18 anos pela data digitada: só adianta o aviso; quem decide é a API (RT-18). */
export function isMinorOn(birthDate: string, today: string = todayIso()): boolean {
  const iso = brDateToIso(birthDate)
  if (!iso || iso > today) return false
  const adultOn = `${Number(iso.slice(0, 4)) + 18}${iso.slice(4)}`
  return today < adultOn
}

// Do cadastro para o formulário ----------------------------------------------------------------

export function patientFormFrom(patient: Patient): PatientFormState {
  const whatsapp = splitWhatsapp(patient.whatsapp)
  const guardianWhatsapp = splitWhatsapp(patient.guardian?.whatsapp ?? null)
  const country = patient.address?.country ?? 'BR'
  return {
    audience: patient.audience,
    // O nome vai para o contrato em caixa de título: o campo mostra como ele sai (RT-17).
    name: titleCase(patient.name),
    birthDate: isoToBrDate(patient.birthDate),
    isForeign: patient.isForeign,
    docNumber: formatDocument(patient.docNumber ?? '', patient.docType),
    foreignDocumentType: patient.foreignDocument?.type ?? '',
    foreignDocumentNumber: patient.foreignDocument?.number ?? '',
    foreignDocumentCountry: patient.foreignDocument?.country ?? '',
    rg: patient.rg ?? '',
    maritalStatus: patient.maritalStatus ?? '',
    occupation: patient.occupation ?? '',
    gender: patient.gender,
    dialCode: whatsapp.dialCode,
    whatsapp: maskWhatsapp(whatsapp.national, whatsapp.dialCode),
    email: patient.email ?? '',
    instagram: patient.instagram ?? '',
    postalCode:
      country === 'BR'
        ? formatPostalCode(patient.address?.postalCode ?? '')
        : (patient.address?.postalCode ?? ''),
    street: patient.address?.street ?? '',
    number: patient.address?.number ?? '',
    complement: patient.address?.complement ?? '',
    district: patient.address?.district ?? '',
    city: patient.address?.city ?? '',
    state: patient.address?.state ?? '',
    country,
    guardianName: patient.guardian ? titleCase(patient.guardian.name) : '',
    guardianDocNumber: formatCPF(patient.guardian?.docNumber ?? ''),
    guardianWhatsapp:
      guardianWhatsapp.dialCode === DEFAULT_DIAL_CODE
        ? formatPhone(guardianWhatsapp.national)
        : patient.guardian
          ? `+${onlyDigits(patient.guardian.whatsapp)}`
          : '',
    imageConsent: patient.imageConsent,
    referralChannel: patient.referralSource?.channel ?? '',
    referralDetail: patient.referralSource?.detail ?? '',
    notes: patient.notes ?? '',
  }
}

// Validação -------------------------------------------------------------------------------------

const MSG_CPF_LENGTH = 'O CPF precisa de 11 dígitos.'
const MSG_CNPJ_LENGTH = 'O CNPJ precisa de 14 dígitos.'
const MSG_CPF_CHECK = 'Esse CPF não é válido. Confira os dois últimos dígitos.'
const MSG_CNPJ_CHECK = 'Esse CNPJ não é válido. Confira os dois últimos dígitos.'

/** CPF para todos; CNPJ só fora do público paciente (RN-PAC-38). As mensagens são as da API. */
const MSG_CNPJ_FOR_PATIENT =
  'O documento de um paciente é o CPF. O CNPJ vale para alunos, funcionários e terceiros.'

function docNumberError(text: string, audience: Audience): string | undefined {
  if (audience === 'patient') {
    if (normalizeDocNumber(text).length === 14) return MSG_CNPJ_FOR_PATIENT
    const digits = onlyDigits(text)
    if (digits.length !== 11) return MSG_CPF_LENGTH
    return isValidCPF(digits) ? undefined : MSG_CPF_CHECK
  }
  const raw = normalizeDocNumber(text)
  if (raw.length === 11 && /^\d+$/.test(raw)) return isValidCPF(raw) ? undefined : MSG_CPF_CHECK
  if (raw.length === 14) return isValidCNPJ(raw) ? undefined : MSG_CNPJ_CHECK
  return raw.length < 12 ? MSG_CPF_LENGTH : MSG_CNPJ_LENGTH
}
const MSG_PHONE = 'O telefone precisa de DDD e número, como 64 99245-2797.'

function whatsappIsValid(text: string, dialCode: string) {
  const length = onlyDigits(text).length
  return dialCode === DEFAULT_DIAL_CODE ? length === 10 || length === 11 : length >= 7
}

/** O WhatsApp do responsável: DDD e número, ou o número inteiro começando por "+". */
function guardianWhatsappIsValid(text: string) {
  const digits = onlyDigits(text)
  if (text.trim().startsWith('+')) return digits.length >= 10 && digits.length <= 13
  return digits.length === 10 || digits.length === 11
}

function birthDateError(text: string, today: string): string | undefined {
  if (text.trim() === '') return undefined
  const iso = brDateToIso(text)
  if (!iso) return 'Use o formato dd/mm/aaaa.'
  if (iso > today) return 'A data de nascimento não pode ser no futuro.'
  if (Number(iso.slice(0, 4)) < 1900) return 'Confira o ano de nascimento.'
  return undefined
}

const hasAny = (...texts: string[]) => texts.some((text) => text.trim() !== '')

/** O endereço vai inteiro: começou a preencher, os campos do contrato passam a ser cobrados. */
export function hasAddress(values: PatientFormState) {
  return hasAny(
    values.postalCode,
    values.street,
    values.number,
    values.complement,
    values.district,
    values.city,
    values.state,
  )
}

export function hasGuardian(values: PatientFormState) {
  return hasAny(values.guardianName, values.guardianDocNumber, values.guardianWhatsapp)
}

export function validatePatientForm(
  values: PatientFormState,
  options: {
    /** Cadastro importado sem WhatsApp: o campo vazio não impede salvar o resto. */
    whatsappOptional?: boolean
    today?: string
  } = {},
): FormErrors<PatientFormState> {
  const errors: FormErrors<PatientFormState> = {}
  const set = (key: PatientFormKey, message: string | undefined) => {
    if (message && !errors[key]) errors[key] = message
  }

  set('name', personNameError(values.name))
  set('birthDate', birthDateError(values.birthDate, options.today ?? todayIso()))

  if (values.isForeign) {
    const number = values.foreignDocumentNumber.trim()
    if (hasAny(number, values.foreignDocumentType, values.foreignDocumentCountry)) {
      if (values.foreignDocumentType === '') {
        set('foreignDocumentType', 'Escolha o tipo do documento na lista.')
      }
      if (number === '') set('foreignDocumentNumber', 'Informe o número do documento.')
      else if (!/^[A-Za-z0-9]{4,20}$/.test(number)) {
        set('foreignDocumentNumber', 'O documento pode ter de 4 a 20 letras e números.')
      }
      if (values.foreignDocumentCountry === '') {
        set('foreignDocumentCountry', 'Escolha o país que emitiu o documento.')
      }
    }
  } else if (values.docNumber.trim() !== '') {
    set('docNumber', docNumberError(values.docNumber, values.audience))
  }

  const rg = values.rg.replace(/[^A-Za-z0-9]/g, '')
  if (values.rg.trim() !== '' && (rg.length < 5 || rg.length > 20)) {
    set('rg', 'O RG pode ter de 5 a 20 letras e números.')
  }
  const occupation = values.occupation.trim()
  if (occupation.length === 1) set('occupation', 'A profissão precisa de pelo menos 2 letras.')
  if (occupation.length > 80) set('occupation', 'A profissão pode ter no máximo 80 caracteres.')

  if (values.whatsapp.trim() === '') {
    if (!options.whatsappOptional) set('whatsapp', 'Informe o telefone com DDD.')
  } else if (!whatsappIsValid(values.whatsapp, values.dialCode)) set('whatsapp', MSG_PHONE)
  if (values.email.trim() !== '') set('email', emailError(values.email))
  if (values.instagram !== '' && !/^[A-Za-z0-9._]{1,30}$/.test(values.instagram)) {
    set('instagram', 'Use só o nome de usuário, sem @.')
  }

  if (hasAddress(values)) {
    const isBrazil = values.country === 'BR'
    const postalCode = values.postalCode.trim()
    if (isBrazil ? onlyDigits(postalCode).length !== 8 : postalCode === '') {
      set(
        'postalCode',
        isBrazil
          ? 'O CEP precisa de 8 dígitos.'
          : 'Informe o código postal, com até 20 caracteres.',
      )
    }
    if (values.street.trim().length < 2) set('street', 'Informe a rua.')
    if (values.number.trim().length < 1) set('number', 'Informe o número, ou s/n.')
    if (values.complement.trim().length > 60) {
      set('complement', 'O complemento pode ter no máximo 60 caracteres.')
    }
    if (values.district.trim().length < 2) set('district', 'Informe o bairro.')
    if (values.city.trim().length < 2) set('city', 'Informe a cidade.')
    const state = values.state.trim().toUpperCase()
    if (isBrazil ? !(STATES as readonly string[]).includes(state) : state === '') {
      set(
        'state',
        isBrazil
          ? 'A UF tem duas letras, como SP.'
          : 'Informe o estado ou a província, com até 60 caracteres.',
      )
    }
  }

  if (hasGuardian(values)) {
    set(
      'guardianName',
      values.guardianName.trim() === ''
        ? 'Informe o nome do responsável.'
        : personNameError(values.guardianName),
    )
    const digits = onlyDigits(values.guardianDocNumber)
    if (digits.length !== 11)
      set('guardianDocNumber', 'O CPF do responsável precisa de 11 dígitos.')
    else if (!isValidCPF(digits)) set('guardianDocNumber', MSG_CPF_CHECK)
    if (!guardianWhatsappIsValid(values.guardianWhatsapp)) {
      set('guardianWhatsapp', 'Informe o telefone do responsável com DDD.')
    }
  }

  if (values.referralChannel === '' && values.referralDetail.trim() !== '') {
    set('referralChannel', 'Escolha como a paciente chegou até a clínica.')
  }
  if (values.notes.length > 2000) {
    set('notes', 'As observações podem ter no máximo 2.000 caracteres.')
  }

  return errors
}

// Do formulário para o contrato -----------------------------------------------------------------

/**
 * O WhatsApp como a API o lê: no Brasil, DDD e número como digitados, e o DDI padrão completa; de
 * outro país, o número inteiro começando por "+", que vale sozinho (o número de fora pode ter
 * menos dígitos do que a API cobra de um número sem DDI).
 */
function whatsappForApi(values: PatientFormState) {
  const text = values.whatsapp.trim()
  if (text === '' || values.dialCode === DEFAULT_DIAL_CODE) return text
  return `${values.dialCode}${onlyDigits(text)}`
}

/** O cadastro inteiro na forma do contrato, com `null` no que está vazio. */
type FullInput = Required<Omit<PatientInput, 'docType' | 'identity'>>

function fullInput(values: PatientFormState): FullInput {
  const isBrazil = values.country === 'BR'
  const docNumber = values.isForeign
    ? ''
    : values.audience === 'patient'
      ? onlyDigits(values.docNumber)
      : normalizeDocNumber(values.docNumber)
  const foreignNumber = values.foreignDocumentNumber.trim().toUpperCase()
  return {
    audience: values.audience,
    name: values.name.trim(),
    whatsapp: whatsappForApi(values),
    dialCode: values.dialCode,
    email: values.email.trim().toLowerCase() || null,
    birthDate: brDateToIso(values.birthDate),
    docNumber: docNumber || null,
    foreignDocument:
      values.isForeign && foreignNumber !== '' && values.foreignDocumentType !== ''
        ? {
            type: values.foreignDocumentType,
            number: foreignNumber,
            country: values.foreignDocumentCountry,
          }
        : null,
    rg: values.rg.replace(/[^A-Za-z0-9]/g, '').toUpperCase() || null,
    gender: values.gender,
    maritalStatus: values.maritalStatus || null,
    occupation: values.occupation.trim() || null,
    address: hasAddress(values)
      ? {
          postalCode: isBrazil ? onlyDigits(values.postalCode) : values.postalCode.trim(),
          street: values.street.trim(),
          number: values.number.trim(),
          complement: values.complement.trim() || null,
          district: values.district.trim(),
          city: values.city.trim(),
          state: isBrazil ? values.state.trim().toUpperCase() : values.state.trim(),
          country: values.country,
        }
      : null,
    guardian: hasGuardian(values)
      ? {
          name: values.guardianName.trim(),
          docType: 'cpf',
          docNumber: onlyDigits(values.guardianDocNumber),
          whatsapp: values.guardianWhatsapp.trim(),
        }
      : null,
    imageConsent: values.imageConsent,
    instagram: values.instagram || null,
    notes: values.notes.trim() || null,
    referralSource:
      values.referralChannel === ''
        ? null
        : { channel: values.referralChannel, detail: values.referralDetail.trim() || null },
  }
}

/** O tipo acompanha o número (T22): 14 caracteres é CNPJ, o resto é CPF (RN-PAC-38). */
function withDocType(input: PatientInput): PatientInput {
  if (typeof input.docNumber !== 'string') return input
  return { ...input, docType: input.docNumber.length === 14 ? 'cnpj' : 'cpf' }
}

/** O corpo do `POST /patients`: só o que foi preenchido. */
export function createPatientInput(values: PatientFormState): PatientInput {
  const full = fullInput(values)
  const input: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(full)) {
    if (value === null) continue
    // Os padrões da API não precisam viajar.
    if ((key === 'gender' || key === 'imageConsent') && value === 'not_provided') continue
    if (key === 'audience' && value === 'patient') continue
    if (key === 'dialCode' && value === DEFAULT_DIAL_CODE) continue
    input[key] = value
  }
  return withDocType(input as PatientInput)
}

/** O cadastro rápido do celular: nome e WhatsApp (RN-PAC-24). */
export function quickPatientInput(values: PatientFormState): PatientInput {
  return {
    ...(values.audience === 'patient' ? {} : { audience: values.audience }),
    name: values.name.trim(),
    whatsapp: whatsappForApi(values),
    ...(values.dialCode === DEFAULT_DIAL_CODE ? {} : { dialCode: values.dialCode }),
  }
}

/**
 * O corpo do `PUT /patients/:id`: só o que mudou em relação ao que a tela abriu (RN-PAC-27).
 * Campo esvaziado vai `null`; `address` e `guardian` vão inteiros; o DDI viaja com o número.
 */
export function updatePatientInput(
  values: PatientFormState,
  initial: PatientFormState,
): PatientInput {
  const next = fullInput(values)
  const previous = fullInput(initial)
  const input: Record<string, unknown> = {}
  for (const key of Object.keys(next) as (keyof FullInput)[]) {
    if (JSON.stringify(next[key]) !== JSON.stringify(previous[key])) input[key] = next[key]
  }
  if ('whatsapp' in input || 'dialCode' in input) {
    input.whatsapp = next.whatsapp
    input.dialCode = next.dialCode
  }
  // O documento novo viaja com o público: a API diz na hora se o CNPJ cabe (RN-PAC-38).
  if (typeof input.docNumber === 'string') input.audience = next.audience
  return withDocType(input as PatientInput)
}

// Do 422 para o formulário ----------------------------------------------------------------------

const FIELD_KEYS: Record<string, PatientFormKey> = {
  audience: 'audience',
  name: 'name',
  birthDate: 'birthDate',
  docNumber: 'docNumber',
  docType: 'docNumber',
  foreignDocument: 'foreignDocumentNumber',
  'foreignDocument.type': 'foreignDocumentType',
  'foreignDocument.number': 'foreignDocumentNumber',
  'foreignDocument.country': 'foreignDocumentCountry',
  rg: 'rg',
  gender: 'gender',
  maritalStatus: 'maritalStatus',
  occupation: 'occupation',
  whatsapp: 'whatsapp',
  dialCode: 'dialCode',
  email: 'email',
  instagram: 'instagram',
  address: 'postalCode',
  'address.postalCode': 'postalCode',
  'address.street': 'street',
  'address.number': 'number',
  'address.complement': 'complement',
  'address.district': 'district',
  'address.city': 'city',
  'address.state': 'state',
  'address.country': 'country',
  guardian: 'guardianName',
  'guardian.name': 'guardianName',
  'guardian.docNumber': 'guardianDocNumber',
  'guardian.docType': 'guardianDocNumber',
  'guardian.whatsapp': 'guardianWhatsapp',
  imageConsent: 'imageConsent',
  referralSource: 'referralChannel',
  'referralSource.channel': 'referralChannel',
  'referralSource.detail': 'referralDetail',
  notes: 'notes',
}

/** O campo do formulário que corresponde a um caminho do 422 (`address.postalCode`). */
export function patientFieldKey(path: string): PatientFormKey | undefined {
  return FIELD_KEYS[path]
}
